import axios from 'axios';

// ============================================================================
// Runtime-safe backend URL resolution
// ----------------------------------------------------------------------------
// Strategy: ALWAYS prefer same-origin (relative path) over any baked-in
// REACT_APP_BACKEND_URL. This guarantees that:
//   - presthyg.app frontend calls presthyg.app/api/... (no CORS issue)
//   - preview frontend calls preview URL/api/... (no CORS issue)
//   - localhost calls localhost/api/... (no CORS issue)
//
// The env var is only used as a last-resort fallback when running outside
// a normal browser (tests, SSR) where window is not available.
// ============================================================================
const resolveBackendBase = () => {
  try {
    if (typeof window !== 'undefined' && window.location && window.location.origin) {
      // Same-origin: safest and always correct. /api is proxied by the
      // infrastructure to the backend on port 8001.
      return ''; // empty = relative path, browser uses current origin
    }
  } catch (e) {
    // Ignore — fall through to env var
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

export const BACKEND_BASE = resolveBackendBase();
const API_URL = BACKEND_BASE + '/api';

export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to every request from localStorage
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

// On 401 → try refresh once → retry → else redirect to login
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only handle 401 (not 403, 429, etc.)
    if (!error.response || error.response.status !== 401) {
      return Promise.reject(error);
    }

    // Don't retry refresh or login requests themselves
    if (originalRequest.url?.includes('/auth/refresh') || originalRequest.url?.includes('/auth/login')) {
      localStorage.removeItem('access_token');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    // Don't retry if already retried
    if (originalRequest._retry) {
      localStorage.removeItem('access_token');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      }).catch((err) => {
        return Promise.reject(err);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Attempt to refresh the access token (uses HttpOnly cookie on same origin)
      const { data } = await axios.post(`${API_URL}/auth/refresh`, {}, { withCredentials: true });
      const newToken = data.access_token;

      if (newToken) {
        localStorage.setItem('access_token', newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);
        return api(originalRequest);
      }
    } catch (refreshError) {
      processQueue(refreshError, null);
      localStorage.removeItem('access_token');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }

    return Promise.reject(error);
  }
);

export const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Une erreur est survenue. Veuillez reessayer.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};
